// The pieces every page is built from, in the one order every page has (Mitch, 2026-11-04: "Hierarchy in design
// matters … the layout should inform the use case mainly, rather than leaving you guessing"):
// 1. `PageHeader`: where you are (crumbs), the title, what the whole page covers (a CM, a project's facts) and the page's
//    own buttons. Never a table's View, Columns, search or filters: those are the table's (DataTable's `bar`).
// 2. `Tabs`: the page's tabs (`level="page"`), or a record's own tabs inside a page (`level="record"`: a run inside a
//    project's Runs, a PO in the side pane), drawn lighter so two rows of tabs never look alike.
// 3. Notices (a banner across the page), then the content: `Figures`, then `PageSection`s, each a table (one unit: its
//    bar, its rows, its count and Copy / CSV) or one box.
// Earlier (Mitch, 2026-11-03: "The projects view is still messy, using a lot of bubble containers … Getting into a run is
// also messy and it's not very coherent"), so every tab reads the same way:
// - `Figures` + `Figure`: a page's key numbers in one strip, label over value, no boxes (Consignment's strip; never a
//   grid of tiles).
// - `PageSection`: a plain heading (title, a note that takes the room left, the table's View and Columns menus, actions)
//   over its content: a `DataTable fit` (its own border is the box) or, `boxed`, anything else in one bordered box.
//   Never a card with a table boxed inside it.
import { AlertTriangle, ChevronRight, Info } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "../lib/router.tsx";
import { cx } from "./controls.tsx";
import type { TableBar } from "./DataTable.tsx";

/** One step back in a page header's crumbs: a link, or a button that changes what the page shows. */
export type Crumb = { label: ReactNode; to?: string; onClick?: () => void; title?: string };

/**
 * A page's header, one row: its crumbs, its title, then `children` (a status badge, what the page covers: a CM picker,
 * a project's facts), and on the right its own `actions` (New, Create orders). Nothing that belongs to one table.
 * `wrap`: where the row doesn't fit (a 1024 px window with a record open beside the page), the actions go onto a row of
 * their own under the title, still on the right, instead of running off the side (the Purchasing plan).
 */
export function PageHeader({
  crumbs,
  title,
  children,
  actions,
  wrap,
}: {
  crumbs?: Crumb[];
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  wrap?: boolean;
}) {
  return (
    <header className={cx("flex shrink-0 items-center gap-3 border-b border-line bg-surface px-4", wrap ? "min-h-12 flex-wrap gap-y-2 py-2" : "h-12")}>
      {crumbs?.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: crumbs are a fixed path
        <span key={i} className="flex min-w-0 shrink items-center gap-2">
          {c.to ? (
            <Link to={c.to} className="truncate text-muted hover:text-text" title={c.title}>
              {c.label}
            </Link>
          ) : (
            <button type="button" onClick={c.onClick} className="truncate text-muted hover:text-text" title={c.title}>
              {c.label}
            </button>
          )}
          <ChevronRight className="size-3.5 shrink-0 text-faint" />
        </span>
      ))}
      <h1 className="min-w-0 shrink-0 truncate text-[15px] font-semibold">{title}</h1>
      {children && <div className="flex min-w-0 items-center gap-3 overflow-hidden text-[12px]">{children}</div>}
      {actions && <div className={cx("ml-auto flex items-center gap-2", wrap ? "min-w-0 flex-wrap justify-end" : "shrink-0")}>{actions}</div>}
    </header>
  );
}

/**
 * A notice across the page, under its header and tabs: one line of what's wrong and what it means, `more` (how to put
 * it right) behind How, and its buttons on the right. One at a time, never a wall of text pushing the table down.
 */
export function Notice({
  tone = "warn",
  children,
  more,
  actions,
}: {
  tone?: "warn" | "danger" | "info";
  children: ReactNode;
  more?: ReactNode;
  actions?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const Icon = tone === "info" ? Info : AlertTriangle;
  return (
    <div
      role="status"
      className={cx(
        "flex shrink-0 items-start gap-2 border-b border-line px-4 py-1.5 text-[12px]",
        tone === "danger" ? "bg-danger-soft text-danger" : tone === "warn" ? "bg-warn-soft text-warn" : "bg-surface-2 text-muted",
      )}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 flex-1 leading-5">
        {children}
        {more && (
          <button type="button" onClick={() => setOpen(!open)} className="ml-1.5 font-medium underline">
            {open ? "Less" : "How"}
          </button>
        )}
        {open && <div className="mt-1 mb-0.5">{more}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export type TabItem<K extends string> = {
  key: K;
  label: ReactNode;
  /** A count after the label ("48", "5 · 3 open"). */
  count?: ReactNode;
  /** A dot after it: something needs a look. Its title says what. */
  dot?: { tone: "warn" | "danger" | "accent"; title?: string };
  /** A badge after it (a number that's news: "3 new"). */
  badge?: ReactNode;
  title?: string;
};

/**
 * Tabs, one way everywhere. `level="page"`: a page's tabs, under its header (underlined, across the page).
 * `level="record"`: a record's own tabs inside a page (a run under the project's Runs, a PO or BOM's tabs), smaller and
 * as pills, so they never read as another row of the page's tabs. `trailing` sits at the end (a record's own button).
 */
export function Tabs<K extends string>({
  tabs,
  active,
  onPick,
  level = "page",
  trailing,
  className,
}: {
  tabs: readonly TabItem<K>[];
  active: K;
  onPick: (key: K) => void;
  level?: "page" | "record";
  trailing?: ReactNode;
  className?: string;
}) {
  const tone = { warn: "bg-warn", danger: "bg-danger", accent: "bg-accent" } as const;
  return (
    <nav
      className={cx(
        "flex shrink-0 items-center overflow-x-auto bg-surface",
        level === "page" ? "gap-1 border-b border-line px-3 text-[13px]" : "gap-1 border-b border-line px-3 py-1.5 text-[12px]",
        className,
      )}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          title={t.title}
          aria-current={active === t.key ? "page" : undefined}
          onClick={() => onPick(t.key)}
          className={cx(
            "flex items-center gap-1.5 whitespace-nowrap",
            level === "page"
              ? cx("-mb-px border-b-2 px-2 py-2", active === t.key ? "border-accent text-text" : "border-transparent text-muted hover:text-text")
              : cx("rounded-md px-2.5 py-1", active === t.key ? "bg-selected font-medium text-text" : "text-muted hover:bg-hover hover:text-text"),
          )}
        >
          {t.label}
          {t.count != null && t.count !== "" && <span className="text-faint">{t.count}</span>}
          {t.badge}
          {t.dot && <span className={cx("size-1.5 rounded-full", tone[t.dot.tone])} title={t.dot.title} />}
        </button>
      ))}
      {trailing && <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">{trailing}</div>}
    </nav>
  );
}

/** One figure in a summary strip: its label over its value, with a few words after the value. */
export function Figure({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "warn" | "danger" | "ok";
  title?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col" title={title}>
      <span className="text-[11px] font-medium tracking-wide whitespace-nowrap text-faint uppercase">{label}</span>
      <span className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
        <span
          className={cx("font-mono text-[15px] font-semibold", tone === "warn" && "text-warn", tone === "danger" && "text-danger", tone === "ok" && "text-ok")}
        >
          {value}
        </span>
        {sub && <span className="min-w-0 truncate text-[12px] text-muted">{sub}</span>}
      </span>
    </div>
  );
}

/** A page's key numbers in one strip (they wrap on a narrow window); `children` are `Figure`s, then anything after them. */
export function Figures({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("flex flex-wrap items-end gap-x-8 gap-y-2 px-1", className)}>{children}</div>;
}

/**
 * A section of a page: its heading (title, and a note that takes the room left), then its content. A function child is
 * a table section: it gets the table's bar with the section's `actions` in it (`(bar) => <DataTable fit bar={bar} …/>`),
 * so the table and everything that acts on it are one unit under the heading. Otherwise the actions end the heading,
 * and `boxed` puts content that isn't a table in one bordered box.
 */
export function PageSection({
  title,
  note,
  actions,
  foot,
  boxed,
  boxClassName,
  className,
  children,
}: {
  title: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  foot?: ReactNode;
  boxed?: boolean;
  /** The box's own classes: `w-fit max-w-full` for a narrow grid (units per variant) that shouldn't stretch. */
  boxClassName?: string;
  className?: string;
  children: ReactNode | ((bar: TableBar) => ReactNode);
}) {
  const table = typeof children === "function";
  const body = table ? children({ actions }) : children;
  return (
    <section className={cx("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex min-h-8 items-center gap-2 px-1">
        <h2 className="min-w-0 truncate font-semibold">{title}</h2>
        {/* The note takes only the room left (no width of its own), so a long note never cuts a short title */}
        {note && (
          <span className="min-w-0 flex-1 basis-0 truncate text-[12px] text-muted" title={typeof note === "string" ? note : undefined}>
            {note}
          </span>
        )}
        {actions && !table && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {boxed ? <div className={cx("min-w-0 rounded-md border border-line bg-surface", boxClassName)}>{body}</div> : body}
      {foot && <div className="px-1 text-[11px] text-faint">{foot}</div>}
    </section>
  );
}
